import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { Sezione } from './SezioneProcedura';
import { Campo, chiave, annunciaSchedaSalvata, togliRipetizione, useCampiAutomatici } from './CampiScheda';
import BottoneRefertoDaScheda from './BottoneRefertoDaScheda';
import RiquadroGravidanze, { dopoQualeSezione } from './RiquadroGravidanze';
import RiquadroAbitudini, { SCHEDE_CON_ABITUDINI } from './RiquadroAbitudini';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import '../../styles/SchedeProcedura.css';

/**
 * Le schede cliniche del medico, compilate dentro una procedura.
 *
 * COSA DISEGNA
 * Una scheda per riquadro - chiuso, come tutti gli altri riquadri della
 * procedura: si apre quello che si compila. Dentro, le sezioni della
 * scheda, e dentro quelle i campi.
 * Le sezioni ripetibili - «Prodotti somministrati» della IV Therapy - si
 * ripetono una volta per prodotto, e ogni ripetizione porta i propri
 * campi: e' cio' che tiene insieme «NAD» con «500 mg».
 *
 * DUE COSE CHE SI VEDONO SOLO QUI
 * 1. Un valore fuori dall'intervallo di riferimento si segnala, non si
 *    rifiuta: una pressione di 160 e' un dato vero e va salvata.
 *    Rifiutarla insegnerebbe a scrivere numeri finti.
 * 2. Le controindicazioni di un prodotto compaiono quando quel prodotto
 *    viene scelto. Sono scritte nel catalogo, accanto alla risposta: il
 *    gestionale le mostra, non le inventa.
 */

const SchedeProcedura = ({ visita }) => {
    const [schede, setSchede] = useState([]);
    const [valori, setValori] = useState({});
    const [righe, setRighe] = useState({});
    /* «Modificato» = diverso dall'istantanea presa al caricamento e a ogni
       salvataggio riuscito, che ricarica (utils/uscitaProtetta.js). */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco({ valori, righe }, versione);
    // scheda_id -> id della compilazione di questa visita, se gia' salvata
    const [compilazioni, setCompilazioni] = useState({});
    const [scrivibile, setScrivibile] = useState(false);
    const [nascosta, setNascosta] = useState(false);
    const [errore, setErrore] = useState(null);
    /* Una scheda NUOVA si compila solo il giorno della procedura (niente di
       retroattivo, decisione di Alex del 23 settembre 2026); quelle gia'
       compilate si risalvano sempre. Lo dice il server, che lo pretende. */
    const [siCompilaOggi, setSiCompilaOggi] = useState(false);

    const visitaId = visita?.id;

    /* I CAMPI SI SALVANO DA SOLI all'uscita (regola di Alex del 24/09/2026):
       niente «Salva la scheda». Un gruppo per scheda. La compilazione di una
       scheda nuova nasce al primo campo salvato - e solo il giorno della
       procedura: lo pretende il server - e allora si rilegge la mappa delle
       compilazioni, che serve a «Scrivi il referto da questa scheda». */
    const compilazioniRef = useRef({});
    const scriviUno = useCallback(async (campoId, riga, valore, schedaId) => {
        const r = await api.put(`/schede/compilazione/${visitaId}/${schedaId}`, { valori: [{ campo_id: campoId, riga, valore }] });
        /* I campi calcolati (migration 314): il database li ricalcola, la
           rotta li rimanda, la schermata li mostra. */
        const calcolati = r?.data?.data?.calcolati;
        if (calcolati && Object.keys(calcolati).length) setValori((p) => ({ ...p, ...calcolati }));
        if (!compilazioniRef.current[schedaId]) {
            const r = await api.get(`/schede/compilazione/${visitaId}`);
            const nuove = r.data?.data?.compilazioni || {};
            compilazioniRef.current = nuove;
            setCompilazioni(nuove);
        }
    }, [visitaId]);
    const auto = useCampiAutomatici(scriviUno, useCallback(() => {
        annunciaSchedaSalvata();
        setVersione((v) => v + 1);
    }, []));
    const { fotografa } = auto;
    useUscitaProtetta(sporco || auto.nonSalvati.length > 0, auto.nonSalvati.length || null);

    const carica = useCallback(async () => {
        if (!visitaId) return;
        try {
            const r = await api.get(`/schede/compilazione/${visitaId}`);
            const d = r.data?.data || {};
            setSchede(d.schede || []);
            setValori(d.valori || {});
            fotografa(d.valori || {}, (d.schede || []).map((x) => String(x.id)));
            compilazioniRef.current = d.compilazioni || {};
            /* Per SEZIONE: con un conto per scheda ogni sezione ripetibile
               disegnava le righe delle altre. */
            setRighe(d.righe_sezioni || {});
            setVersione((v) => v + 1);
            setCompilazioni(d.compilazioni || {});
            setScrivibile(d.scrivibile === true);
            setSiCompilaOggi(d.si_compila_oggi === true);
            setNascosta(d.riservata_nascosta === true);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere le schede');
        }
    }, [visitaId, fotografa]);

    useEffect(() => { carica(); }, [carica]);

    const quanteRighe = useCallback((sez) => Math.max(1, righe[sez.id] || 1), [righe]);

    const cambia = (campoId, riga, v) => {
        setValori((precedenti) => ({ ...precedenti, [chiave(campoId, riga)]: v }));
    };

    const aggiungiRiga = (sez) =>
        setRighe((p) => ({ ...p, [sez.id]: quanteRighe(sez) + 1 }));

    /* Toglie UNA ripetizione di UNA sezione: il server tocca solo i campi
       di quella sezione e fa scalare le righe dopo. A schermo la stessa
       cosa, senza ricaricare: quello che e' scritto e non ancora salvato
       nelle altre righe e nelle altre schede resta dov'e'. */
    const togliRiga = async (scheda, sez, riga) => {
        if (!window.confirm(`Togliere «${sez.ripetibile_etichetta} ${riga}»? `
                          + 'Le altre risposte della scheda restano.')) return;
        const eraPulita = !sporco;
        try {
            await api.delete(`/schede/compilazione/${visitaId}/${scheda.id}/riga/${riga}`,
                             { params: { sezione: sez.id } });
            setValori((p) => { const n = togliRipetizione(p, sez, riga); fotografa(n, schede.map((x) => String(x.id))); return n; });
            setRighe((p) => ({ ...p, [sez.id]: Math.max(1, quanteRighe(sez) - 1) }));
            if (eraPulita) setVersione((v) => v + 1);
        } catch (e) {
            setErrore(e?.message || 'Non riesco a togliere la ripetizione');
        }
    };

    const vuoto = useMemo(() => !schede.length, [schede]);

    /* Si scrive in una scheda se si vede la procedura e, per una scheda
       che non e' ancora stata compilata, se la procedura e' di oggi. */
    const scriviQui = (s) => scrivibile && (!!compilazioni[s.id] || siCompilaOggi);
    const giornoProcedura = String(visita?.data_visita || '').slice(0, 10).split('-').reverse().join('/');

    if (nascosta) return null;
    if (vuoto) return null;

    return (
        <>
            {errore && <p className="sp-errore">{errore}</p>}
            {schede.map((s) => (
                <Sezione key={s.id} title={s.nome}
                         right={s.area_nome ? <span className="sp-area">{s.area_nome}</span> : null}>
                    {/* Una scheda che vale solo per un sesso, su un paziente
                        senza sesso in anagrafica (migration 235): al posto dei
                        campi la scritta. La regola la decide il server. */}
                    {s.ammessa === false && s.motivo === 'sesso_mancante' ? (
                        <p className="sp-segnaposto">Sesso non indicato in anagrafica</p>
                    ) : (<>
                    {/* Una scheda senza nessun campo e' un segnaposto: il
                        riquadro c'e' perche' si sappia che quella cosa va
                        registrata, ma i campi non sono stati ancora decisi.
                        Dirlo in chiaro evita che qualcuno la scambi per una
                        scheda rotta e la segnali come guasto. */}
                    {s.sezioni.every((z) => !z.campi.length) && (
                        <p className="sp-segnaposto">
                            Questa scheda non ha ancora campi: il riquadro è pronto,
                            il contenuto si compone dal <b>catalogo dei campi</b>.
                        </p>
                    )}

                    {/* Fumo e alcol: il valore dell'anamnesi al giorno della
                        scheda, in sola lettura (proposta 16). */}
                    {SCHEDE_CON_ABITUDINI.includes(s.codice) && (
                        <RiquadroAbitudini pazienteId={visita.paziente_id}
                            visitaId={visita.id} compilazioneId={compilazioni[s.id] || null} />
                    )}

                    {s.sezioni.filter((z) => z.campi.length > 0).map((sez) => (
                      <React.Fragment key={sez.id}>
                        <div className="sp-sezione">
                            <div className="sp-sezione-titolo">{sez.titolo}</div>

                            {sez.ripetibile ? (
                                <>
                                    {Array.from({ length: quanteRighe(sez) }, (_, i) => i + 1).map((riga) => (
                                        <div className="sp-ripetizione" key={riga}>
                                            <div className="sp-ripetizione-testa">
                                                <span>{sez.ripetibile_etichetta} {riga}</span>
                                                {scriviQui(s) && quanteRighe(sez) > 1 && (
                                                    <button type="button" onClick={() => togliRiga(s, sez, riga)}
                                                            title="Togli questa ripetizione">
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="sp-campi">
                                                {sez.campi.map((c) => (
                                                    <Campo key={c.campo_id} campo={c} riga={riga}
                                                           valore={valori[chiave(c.campo_id, riga)]}
                                                           bloccato={!scriviQui(s)}
                                                           onChange={(v) => cambia(c.campo_id, riga, v)}
                                                           onEsci={(v) => auto.esci(c.campo_id, riga, v, String(s.id))}
                                                           salvataggio={auto.di(c.campo_id, riga, String(s.id))}
                                                           onRiprova={() => auto.riprova(c.campo_id, riga, valori[chiave(c.campo_id, riga)], String(s.id))} />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {scriviQui(s) && (
                                        <button type="button" className="sp-aggiungi"
                                                onClick={() => aggiungiRiga(sez)}>
                                            <Plus size={13} aria-hidden="true" /> Aggiungi {sez.ripetibile_etichetta?.toLowerCase()}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="sp-campi">
                                    {sez.campi.map((c) => (
                                        <Campo key={c.campo_id} campo={c} riga={1}
                                               valore={valori[chiave(c.campo_id, 1)]}
                                               bloccato={!scriviQui(s)}
                                               onChange={(v) => cambia(c.campo_id, 1, v)}
                                               onEsci={(v) => auto.esci(c.campo_id, 1, v, String(s.id))}
                                               salvataggio={auto.di(c.campo_id, 1, String(s.id))}
                                               onRiprova={() => auto.riprova(c.campo_id, 1, valori[chiave(c.campo_id, 1)], String(s.id))} />
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Gravidanze, climaterio e menopausa: il riquadro
                            dell'anamnesi, lo stesso dato (RiquadroGravidanze).
                            Oggi la scheda ginecologica sta nel profilo; se la
                            si sposta qui, il riquadro la segue. */}
                        {dopoQualeSezione(s.codice, s.sezioni) === sez.id && (
                            <RiquadroGravidanze pazienteId={visita.paziente_id} origine="scheda_ginecologica"
                                visitaId={visita.id} compilazioneId={compilazioni[s.id] || null} />
                        )}
                      </React.Fragment>
                    ))}
                    {dopoQualeSezione(s.codice, s.sezioni) === 'fondo' && (
                        <RiquadroGravidanze pazienteId={visita.paziente_id} origine="scheda_ginecologica"
                            visitaId={visita.id} compilazioneId={compilazioni[s.id] || null} />
                    )}

                    {scrivibile && !scriviQui(s) && s.sezioni.some((z) => z.campi.length > 0) && (
                        <p className="sp-segnaposto">
                            Questa scheda non è stata compilata il giorno della procedura, il {giornoProcedura}:
                            una scheda si compila il giorno della procedura. Niente di retroattivo.
                        </p>
                    )}
                    {scriviQui(s) && s.sezioni.some((z) => z.campi.length > 0) && (
                        <div className="sp-azioni">
                            {/* Solo dopo il primo campo salvato: prima la
                                compilazione non esiste e non c'e' niente da copiare. */}
                            <BottoneRefertoDaScheda compilazioneId={compilazioni[s.id]} disabilitato={auto.nonSalvati.length > 0} />
                        </div>
                    )}
                    </>)}
                </Sezione>
            ))}
        </>
    );
};

export default SchedeProcedura;
